import { useParams, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import React, { useEffect, useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { cargarLogo, crearPDF } from "../utils/pdfUtils"; // Mantén tus utilidades
import logo from "../assets/logo.png"; // Ajusta la ruta según tu proyecto

function PdfTicket() {
  const { id } = useParams();
  const [pdfTicket, setPdfTicket] = useState({});
  const [resolucion, setResolucion] = useState("");
  const [emailCliente, setEmailCliente] = useState("");
  const sigCanvas = useRef();
  const firmaContainerRef = useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(
    window.innerWidth < 768 ? 300 : 500
  );
  const [tecnicos, setTecnicos] = useState([]);
  const [estado, setEstado] = useState("No Terminada");
  const [tecnicosSeleccionados, setTecnicosSeleccionados] = useState([]);
  const navigate = useNavigate();

  // ✅ Obtener datos del ticket
  useEffect(() => {
    axios
      .get(`/api/pdfTicket/${id}`)
      .then((res) => setPdfTicket(res.data))
      .catch((err) => console.error("Error al obtener el ticket:", err));
  }, [id]);

  // ✅ Ajustar ancho del canvas dinámicamente
  useEffect(() => {
    const actualizarAncho = () => {
      if (firmaContainerRef.current) {
        setCanvasWidth(Math.min(firmaContainerRef.current.offsetWidth, 500));
      }
    };
    actualizarAncho();
    window.addEventListener("resize", actualizarAncho);
    return () => window.removeEventListener("resize", actualizarAncho);
  }, []);

  // ✅ Obtener lista de técnicos
  useEffect(() => {
    axios
      .get("/api/tecnicos")
      .then((res) => setTecnicos(res.data))
      .catch((err) => console.error("Error al obtener técnicos:", err));
  }, []);

  // ✅ Generar PDF (mantén tu lógica)
  const generarPDF = async () => {
    const firmaBase64 = sigCanvas.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    const logoBase64 = await cargarLogo(logo);
    return await crearPDF({
      pdfTicket,
      resolucion,
      firmaBase64,
      logoBase64,
      tecnicosSeleccionados,
      estado,
      emailCliente,
    });
  };

  // ✅ Enviar PDF (igual que tu código original)
  const enviarPDF = async () => {
    if (!resolucion.trim()) return alert("Introduce la resolución.");
    if (!emailCliente.includes("@")) return alert("Email inválido.");
    if (tecnicosSeleccionados.length === 0)
      return alert("Selecciona al menos un técnico.");
    if (sigCanvas.current.isEmpty())
      return alert("Por favor, firma antes de enviar.");

    const doc = await generarPDF();
    const pdfBlob = doc.output("blob");

    const formData = new FormData();
    formData.append(
      "pdf",
      pdfBlob,
      `Parte de Abonado Nº ${pdfTicket.cliente_id}.pdf`
    );
    formData.append("ticketId", pdfTicket.ticket_id); // ✅ Alias correcto
    formData.append("email", emailCliente);
    formData.append("estado", estado);
    formData.append("resolucion", resolucion);
    formData.append("ncontacto", pdfTicket.ncontacto);
    formData.append("tecnicos", JSON.stringify(tecnicosSeleccionados));

    try {
      await axios.post("/api/enviar-pdf", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        withCredentials: true,
      });
      navigate("/ticket");
    } catch (error) {
      console.error("Error al enviar el PDF:", error);
      const mensaje =
        error.response?.data?.message || "Error desconocido al enviar el PDF.";
      alert(mensaje);
    }
  };

  if (!pdfTicket.ticket_id) return <p className="p-4">Cargando ticket...</p>;

  return (
    <div className="xl:p-3 md:p-6 max-w-5xl mx-auto min-h-[calc(100vh-6rem)]">
      {/* Datos del ticket con alias correctos */}
      <div className="mb-2">
        <p>
          <strong>Abonado:</strong>{" "}
          <span className="text-mid">{pdfTicket.cliente_id}</span>
        </p>
        <p>
          <strong>Cliente:</strong>{" "}
          <span className="text-mid">{pdfTicket.nombre}</span>
        </p>
        <p>
          <strong>Dirección:</strong>{" "}
          <span className="text-mid">{pdfTicket.direccion}</span>
        </p>
        <p>
          <strong>Población:</strong>{" "}
          <span className="text-mid">{pdfTicket.poblacion}</span>
        </p>
        <p>
          <strong>Tipo de Avería:</strong>{" "}
          <span className="text-mid">{pdfTicket.tipoaveria}</span>
        </p>
        <p>
          <strong>Descripción:</strong>{" "}
          <span className="text-mid">{pdfTicket.descripcion}</span>
        </p>
      </div>

      {/* Check Avería */}
      <label>
        <input
          type="checkbox"
          checked={estado === "Terminada"}
          onChange={(e) =>
            setEstado(e.target.checked ? "Terminada" : "No Terminada")
          }
          className="me-2 mb-3"
        />
        Avería terminada
      </label>

      {/* Select Técnicos */}
      <div className="mb-1">
        <label className="block font-semibold mb-1">Técnico:</label>
        <select
          onChange={(e) => {
            const seleccionado = JSON.parse(e.target.value);
            const yaExiste = tecnicosSeleccionados.some(
              (t) => t.id === seleccionado.id
            );
            if (seleccionado && !yaExiste) {
              setTecnicosSeleccionados([
                ...tecnicosSeleccionados,
                seleccionado,
              ]);
            }
          }}
          className="w-full p-2 mb-1 border rounded text-black"
        >
          <option value="">Selecciona un técnico</option>
          {tecnicos.map((tecnico) => (
            <option key={tecnico.id} value={JSON.stringify(tecnico)}>
              {tecnico.username}
            </option>
          ))}
        </select>

        {tecnicosSeleccionados.length > 0 && (
          <div className="mt-2 text-sm text-white">
            <strong>Técnicos asignados:</strong>
            <ul className="list-disc ml-5 mt-1">
              {tecnicosSeleccionados.map((tecnico, index) => (
                <li
                  key={index}
                  className="cursor-pointer hover:text-red-400 transition mb-2"
                  onClick={() =>
                    setTecnicosSeleccionados(
                      tecnicosSeleccionados.filter((t) => t.id !== tecnico.id)
                    )
                  }
                >
                  {tecnico.username}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Resolución */}
      <div className="mb-3">
        <label className="block font-semibold mb-1">Resolución:</label>
        <textarea
          className="w-full p-2 text-black font-semibold border rounded"
          rows="5"
          value={resolucion}
          onChange={(e) => setResolucion(e.target.value)}
        />
      </div>

      {/* Email Cliente */}
      <div className="mb-4">
        <label className="block font-semibold mb-1">Email del cliente:</label>
        <input
          type="email"
          className="w-auto p-2 text-black font-semibold border rounded"
          value={emailCliente}
          onChange={(e) => setEmailCliente(e.target.value)}
          placeholder="cliente@ejemplo.com"
        />
      </div>

      {/* Firma Cliente */}
      <div className="mb-4" ref={firmaContainerRef}>
        <label className="block font-semibold mb-1">Firma del cliente:</label>
        <SignatureCanvas
          ref={sigCanvas}
          penColor="black"
          canvasProps={{
            width: canvasWidth,
            height: 150,
            className: "border rounded bg-white",
          }}
        />
        <div className="grid grid-cols-2 w-full max-w-md gap-2 mt-4">
          {/* Botón enviar */}
          <button
            type="button"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={enviarPDF}
          >
            Enviar PDF
          </button>
          <button
            type="button"
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={() => sigCanvas.current.clear()}
          >
            Limpiar firma
          </button>
        </div>
      </div>
    </div>
  );
}

export default PdfTicket;
